import React, { 
    FC,
    useEffect,
    useState,
    useRef,
} from 'react';

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { Error } from '@mui/icons-material';
import Box from "@mui/material/Box";

import WebFont from 'webfontloader';

import Close from '@pixel/assets/icons/Close';

import Button from '@pixel/core/Button';
import Typography from '@pixel/core/Typography';
import TextField from '@pixel/core/TextField';

import SignatureText, { SignatureTextOptions, CanvasRef } from '../components/SignatureText';
import ErrorBoundary from '../components/ErrorBoundary';
import {
    useMultipleLocalizedText,
} from '../contexts/Localization/localizationHooks';
import useTodaysDate from '../hooks/useTodaysDate';
import dataURLtoBlob from '../utils/dataURLToBlob';

export const SIGNATURE_TEXT_OPTIONS: SignatureTextOptions = {
    fontFamily: 'Italianno',
    fontSize: 24,
    fontColor: 'black',
    marginHorizontal: 10,
    marginVertical: 10,
}

export const FIRST_LAST_CHAR_LIMIT = 100
export const INITIALS_CHAR_LIMIT = 100

export interface OnSignData {
    firstLastName: string
    initials: string
    fullNameSignature: Blob
    initialsSignature: Blob
}
export type OnSignEvent = (event: MouseEvent, data: OnSignData) => void

interface SignDocumentProps {
    open: boolean
    loading?: boolean
    error?: string

    firstName?: string
    lastName?: string
    initials?: string

    onClose?: () => void
    onSign?: OnSignEvent

    ariaLabelTitle?: string
    ariaLabelDesciption?: string
    ariaLabelCloseModal?: string
}

export const SignDocument: FC<SignDocumentProps> = ({
    open,
    loading,
    error,

    firstName = '',
    lastName = '',
    initials,

    onClose = () => {},
    onSign,

    ariaLabelTitle = 'alert-dialog-title',
    ariaLabelDesciption = 'alert-dialog-description',
    ariaLabelCloseModal = 'close',
}) => {
    const todaysDate = useTodaysDate()
    const {
        CREATESIGNATURE,
        TODAYSDATE,
        FULLLEGALNAME,
        INITIALS,
        PREVIEW,
        LEGALREPRESENTATION,
        SIGNELECTRONICALLY,
        ERROR_SIGNATURECANVAS,
        ERROR_FIRSTLASTBLANK,
        ERROR_FIRSTLASTCHARLIMIT,
        ERROR_INITIALSBLANK,
        ERROR_INITIALSCHARLIMIT,
        ERROR_SIGNATUREBLANK,
        ERROR_SIGNATUREGENERATION,
        ERROR_SIGNATURESAVE,
    } = useMultipleLocalizedText()

    const firstLastSignatureRef = useRef<CanvasRef>(null)
    const initialsSignatureRef = useRef<CanvasRef>(null)
    const [firstLastSignatureError, setFirstLastSignatureError] = useState('')
    const [initialsSignatureError, setInitialsSignatureError] = useState('')

    const [firstLastInput, setFirstLastInput] = useState(() => (firstName || '').trim() + ' ' + (lastName || '').trim())
    const [firstLastError, setFirstLastError] = useState('')

    const [initialsInput, setInitialsInput] = useState(() => {
        if (initials && typeof initials === 'string') {
            return initials
        }

        if (typeof firstName !== 'string' || firstName.length === 0 || typeof lastName !== 'string' || lastName.length === 0) {
            return ''
        }

        return firstName.trim().charAt(0) + '' + lastName.trim().charAt(0)
    })
    const [initialsError, setInitialsError] = useState('')

    const [ready, setReady] = useState(false);

    useEffect(()=>{
        // Load any required fonts
        WebFont.load({
          google: {
              families: ['Italianno']
          },
           active: () => {
              setReady(true)
           },
           timeout: 10000
          });
      },[]);

    const hasError = (): boolean => {
        setFirstLastError('')
        setInitialsError('')
        setFirstLastSignatureError('')
        setInitialsSignatureError('')

        let hasError = false

        if (!firstLastInput) {
            setFirstLastError(ERROR_FIRSTLASTBLANK)
            hasError = true
        }

        if (firstLastInput.length > FIRST_LAST_CHAR_LIMIT) {
            const firstLastErrorText = (ERROR_FIRSTLASTCHARLIMIT || '').replace(':length', String(FIRST_LAST_CHAR_LIMIT))
            setFirstLastError(firstLastErrorText)
            hasError = true
        }

        if (!initialsInput) {
            setInitialsError(ERROR_INITIALSBLANK)
            hasError = true
        }

        if (initialsInput.length > INITIALS_CHAR_LIMIT) {
            const initialsErrorText = (ERROR_INITIALSCHARLIMIT || '').replace(':length', String(INITIALS_CHAR_LIMIT))
            setInitialsError(initialsErrorText)
            hasError = true
        }

        if (firstLastSignatureRef.current.isEmpty()) {
            setFirstLastSignatureError(ERROR_SIGNATUREBLANK)
            hasError = true
        }

        if (initialsSignatureRef.current.isEmpty()) {
            setInitialsSignatureError(ERROR_SIGNATUREBLANK)
            hasError = true
        }

        return hasError
    }

    const handleOnSign = (e: any) => {
        if (hasError()) {
            return
        }

        let signatureFirstLastBlob = null
        try {
            signatureFirstLastBlob = dataURLtoBlob(firstLastSignatureRef.current.toDataURL('data:image/png;base64'))
        } catch(e) {
            console.error(e)
            setFirstLastSignatureError(ERROR_SIGNATUREGENERATION)
            return
        }

        let signatureInitialsBlob = null
        try {
            signatureInitialsBlob = dataURLtoBlob(initialsSignatureRef.current.toDataURL('data:image/png;base64'))
        } catch(e) {
            console.error(e)
            setInitialsSignatureError(ERROR_SIGNATUREGENERATION)
            return
        }

        if (typeof onSign === 'function') {
            onSign(e, {
                firstLastName: firstLastInput,
                initials: initialsInput,
                fullNameSignature: signatureFirstLastBlob,
                initialsSignature: signatureInitialsBlob,
            })
        }
    }

    const handleClose = () => {
        if (!loading) {
            onClose()
        }
    }

    const todaysDateString = (TODAYSDATE || '').replace(':date', todaysDate)

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby={ariaLabelTitle}
            aria-describedby={ariaLabelDesciption}
            PaperProps={{sx: {maxWidth: "none"}}}
        >
            <DialogTitle>
                {CREATESIGNATURE}
                <IconButton
                    aria-label={ariaLabelCloseModal}
                    onClick={handleClose}
                    disabled={loading}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                    <div>
                        <Typography variant="subtitle2" sx={{ marginTop: '16px', marginBottom: '16px' }}>{todaysDateString}</Typography>
                        <div style={{ display: 'flex', width: '100%' }}>
                            <TextField 
                                label={FULLLEGALNAME}
                                value={firstLastInput}
                                onChange={(e: any) => {
                                    if (e.target.value.length > FIRST_LAST_CHAR_LIMIT) return

                                    setFirstLastInput(e.target.value)
                                }}
                                fullWidth
                                sx={{ marginRight: '4px' }}
                                error={Boolean(firstLastError)}
                                errorText={firstLastError}
                            />
                            <TextField 
                                label={INITIALS}
                                value={initialsInput}
                                onChange={(e: any) => {
                                    if (e.target.value.length > INITIALS_CHAR_LIMIT) return

                                    setInitialsInput(e.target.value)
                                }}
                                fullWidth
                                sx={{ marginLeft: '4px' }}
                                error={Boolean(initialsError)}
                                errorText={initialsError}
                            />
                        </div>
                        <Typography variant="subtitle2" sx={{ marginTop: '16px' }}>{PREVIEW}</Typography>
                        {
                            ready?
                            <div style={{ display: 'flex' }}>
                                <ErrorBoundary text={ERROR_SIGNATURECANVAS}>
                                    <SignatureText
                                        ref={firstLastSignatureRef}
                                        text={firstLastInput}
                                        textOptions={SIGNATURE_TEXT_OPTIONS}
                                    />
                                </ErrorBoundary>
                                <ErrorBoundary text={ERROR_SIGNATURECANVAS}>
                                    <SignatureText
                                        ref={initialsSignatureRef}
                                        text={initialsInput}
                                        textOptions={SIGNATURE_TEXT_OPTIONS}
                                    />
                                </ErrorBoundary>
                            </div>
                         :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: "center"}}>
                                <CircularProgress size="17px"/>
                            </Box>
                        }
                        { ((firstLastSignatureError && firstLastInput) || (initialsSignatureError && initialsInput)) && <Typography variant="error" sx={{ color: 'red' }}>{firstLastSignatureError || initialsSignatureError}</Typography> }
                    </div>
            </DialogContent>
            <DialogActions>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <Typography variant="subtitle2" sx={{ marginRight: '8px' }}>{LEGALREPRESENTATION}</Typography>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleOnSign}
                        disabled={loading || !ready}
                        sx={{ marginRight: loading ? '4px' : undefined }}
                    >
                        {SIGNELECTRONICALLY}
                    </Button>
                    {
                        loading && (
                            <CircularProgress 
                                size="17px"
                            />
                        )
                    }
                    {
                        !loading && error && (
                            <Tooltip title={ERROR_SIGNATURESAVE} placement="top" arrow sx={{ marginLeft: '8px' }}>
                                <Error htmlColor="red" />
                            </Tooltip>
                        )
                    }
                </div>
            </DialogActions>
        </Dialog>
    )
}

export default SignDocument
